import { MenuPosition } from 'react-select';
import { Button, ButtonProps } from 'src/components/atoms';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { textStyleObject } from 'src/theme/atlas';
import { FormState } from 'src/types';
import styled, { css, DefaultTheme } from 'styled-components';

export const SelectWrapper = styled.div<{
  disabled?: boolean;
  alignRight?: boolean;
  $zIndex?: number;
}>`
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  width: ${(props) => (props.alignRight ? 'fit-content' : '100%')};
  z-index: ${(props) => props.$zIndex ?? 'inherit'};
`;

export const DropdownIndicatorIcon = styled(StyledIcon)<{
  disabled?: boolean;
  buttonVariant?: 'primary' | 'secondary' | 'tertiary';
}>`
  color: ${(props) =>
    props.disabled
      ? props.theme?.atlas.semantic.color.outline.default
      : props.buttonVariant === 'primary'
        ? props.theme?.atlas.semantic.color.action.primary.onPrimary
        : props.theme?.atlas.semantic.color.type.default};
`;

export const MultiSelectOptionContainer = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
`;

export const MultiSelectComponentContainer = styled.div<{ isMulti?: boolean }>`
  margin: ${(props) => (props.isMulti ? '3px' : '0px 3px 0px 0px')};
`;

export const menuStyle = (
  width?: string,
  alignRight?: boolean,
  theme?: DefaultTheme,
) => ({
  zIndex: 2,
  border: `none`,
  backgroundColor: theme?.atlas.semantic.color.surface.default,
  borderRadius: `3px`,
  ...theme?.boxShadows.shadowLarge,
  width: width ?? 'max-content',
  minWidth: width ?? '100%',
  ...(alignRight ? { right: 0, position: 'absolute' as MenuPosition } : {}),
});

export const menuListStyle = (menuHeight?: string) => ({
  ...(menuHeight ? { maxHeight: menuHeight } : {}),
  paddingTop: 0,
  paddingBottom: 0,
  borderRadius: '3px',
});

export const controlStyle = (
  isFocused: boolean,
  state: FormState,
  isMulti: boolean,
  empty: boolean,
  width?: string,
  theme?: DefaultTheme,
) => {
  const focusShadow = theme?.isVaultVariant
    ? theme.boxShadows.focusState
    : theme?.atlas.semantic.shadow.focus;
  const control = {
    display: 'flex',
    flexDirection: 'row' as const,
    ...textStyleObject(theme!.atlas.typography.semantic.bodySmall),
    color: theme?.atlas.semantic.color.type.default,
    minWidth: width || '110px',
    boxShadow: 'none',
    borderRadius: `3px`,
    backgroundColor: theme?.atlas.semantic.color.surface.default,
    border: `1px solid ${theme?.atlas.semantic.color.outline.default}`,
    ...(isMulti
      ? {
          minHeight: '34px',
          padding: empty ? '2px 2px 2px 16px' : '0px',
        }
      : {
          minHeight: '40px',
          padding: '1px 2px 1px 16px',
        }),
  };
  switch (state) {
    case FormState.DISABLED: {
      return {
        ...control,
        backgroundColor: theme?.atlas.semantic.color.surface.default,
        border: `1px solid ${theme?.atlas.semantic.color.outline.disabled}`,
      };
    }
    case FormState.READONLY: {
      return {
        ...control,
        backgroundColor: theme?.atlas.semantic.color.surface.variant,
        border: `1px solid ${theme?.atlas.semantic.color.outline.default}`,
      };
    }
    case FormState.SUCCESS: {
      return {
        ...control,
        ...(theme?.isVaultVariant
          ? {
              backgroundColor:
                theme.atlas.semantic.color.status.success.background,
              border: `1px solid ${theme.atlas.semantic.color.status.success.text}`,
            }
          : {}),
        ...(isFocused
          ? {
              boxShadow: focusShadow,
            }
          : {}),
      };
    }
    case FormState.ERROR: {
      return {
        ...control,
        backgroundColor: theme?.atlas.semantic.color.status.error.background,
        border: `1px solid ${theme?.atlas.semantic.color.status.error.default}`,
        ...(isFocused
          ? {
              boxShadow: focusShadow,
            }
          : {}),
      };
    }
    case FormState.WARNING: {
      return {
        ...control,
        backgroundColor: theme?.atlas.semantic.color.status.warning.background,
        border: `1px solid ${theme?.atlas.semantic.color.status.warning.default}`,
        ...(isFocused
          ? {
              boxShadow: focusShadow,
            }
          : {}),
      };
    }
    default: {
      return {
        ...control,
        ...(isFocused
          ? {
              boxShadow: focusShadow,
            }
          : {}),
      };
    }
  }
};

export const optionStyle = (
  isFocused: boolean,
  isSelected: boolean,
  hasCustomOptionComponent: boolean,
  isDisabled = false,
  _isMulti = false,
  theme?: DefaultTheme,
) => {
  const getBackgroundColor = () => {
    if (isDisabled) return theme?.atlas.semantic.color.surface.subtle;
    if (isSelected)
      return theme?.atlas.semantic.color.selection.secondary.default;
    if (isFocused)
      return theme?.atlas.semantic.color.action.secondary.hoverFill;
    return theme?.atlas.semantic.color.surface.default;
  };

  return {
    padding: '8px 10px 8px 10px',
    height: hasCustomOptionComponent ? 'auto' : '40px',

    backgroundColor: getBackgroundColor(),

    color: isDisabled
      ? theme?.atlas.semantic.color.outline.default
      : theme?.atlas.semantic.color.type.default,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    ...textStyleObject(theme!.atlas.typography.semantic.bodySmall),
    textAlign: 'left',
  } as const;
};

export const valueStyle = (
  state: FormState,
  renderValue: boolean,
  theme?: DefaultTheme,
) => ({
  padding: '0',
  color:
    state === FormState.DISABLED || state === FormState.READONLY
      ? theme?.atlas.semantic.color.type.muted
      : theme?.atlas.semantic.color.type.default,
  ...(renderValue ? {} : { width: '0' }),
});

export const singleValueStyle = (
  state: FormState,
  renderValue: boolean,
  theme?: DefaultTheme,
) => ({
  color:
    state === FormState.DISABLED || state === FormState.READONLY
      ? theme?.atlas.semantic.color.type.disabled
      : theme?.atlas.semantic.color.type.default,
  ...textStyleObject(theme!.atlas.typography.semantic.bodySmall),
  margin: 0,
  ...(renderValue ? {} : { visibility: 'hidden' as const, width: '0' }),
});

export const ButtonWithIcon = styled(Button)<{
  noBorder?: boolean;
  active?: boolean;
}>`
  display: flex;
  column-gap: 8px;
  ${(props) => props.noBorder && `border: none;`}
  ${(props) =>
    props.active &&
    !props.loading &&
    css<ButtonProps>`
      background-color: ${(props) => {
        if (props.disabled) {
          return props.theme?.atlas.semantic.color.action.primary.disabled;
        }
        switch (props.variant) {
          case 'primary':
            return props.theme?.atlas.semantic.color.action.primary.active;
          case 'error':
            return props.theme?.atlas.semantic.color.action.destructive.active;
          default:
            return props.theme?.atlas.semantic.color.action.secondary.hoverFill;
        }
      }};
    `}
`;
